import React, { useState, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import HeroImage from './HeroImage';
import { getPhotoPool, PhotoPoolItem } from '../../services/plansApi';
import { useUserStore } from '../../stores';
import './BrowseableImage.css';

interface BrowseableImageProps {
  src?: string;
  fallbackKey: string;
  queryKey?: string;
  size?: 'thumb-sm' | 'thumb-lg' | 'hero';
  className?: string;
  /** Who took `src`. Required alongside it — see `onAttributionChange`. */
  attribution?: { photographer: string; unsplash_link: string } | null;
  /** Called with the credit for whatever is CURRENTLY shown (r774 finding 1).
   *  Browsing swaps in a different pool row, so the credit the parent renders
   *  has to swap with it: this component knew whose photograph it had put on
   *  screen and told nobody, which credited the wrong person the moment
   *  anyone pressed an arrow.
   *
   *  REQUIRED, not optional (r776 finding 4). Optional, it made a caller's
   *  compliance voluntary — a new `<BrowseableImage>` could display pool
   *  photography and credit nobody without any file the boundary scans
   *  changing. That is the same future-reachability premise that was
   *  correctly rejected for `PlanCard`. TypeScript is a stronger boundary
   *  than a lexical scan here, so it holds this one: you cannot render this
   *  component without deciding what to do with the credit. */
  onAttributionChange: (
    attribution: { photographer: string; unsplash_link: string } | null) => void;
  /** Passed through to `HeroImage`: whether its paper fallback is what is on
   *  screen, after a load failure too (r1123 finding 1). */
  onBareChange?: (bare: boolean) => void;
  /** Passed through to `HeroImage`: false when the caller draws its own
   *  title over the image, so the paper fallback does not repeat it. */
  showLabel?: boolean;
}

const STORAGE_PREFIX = 'vizday_browse_v1';

function getStoredOffset(queryKey: string, serverDate: string): number {
  try {
    const key = `${STORAGE_PREFIX}:${queryKey}:${serverDate}`;
    const val = localStorage.getItem(key);
    return val ? parseInt(val, 10) || 0 : 0;
  } catch { return 0; }
}

function setStoredOffset(queryKey: string, serverDate: string, offset: number) {
  try {
    localStorage.setItem(`${STORAGE_PREFIX}:${queryKey}:${serverDate}`, String(offset));
  } catch { /* localStorage unavailable */ }
}

const BrowseableImage: React.FC<BrowseableImageProps> = ({
  src,
  fallbackKey,
  queryKey,
  size = 'hero',
  className,
  attribution,
  onAttributionChange,
  onBareChange,
  showLabel,
}) => {
  const user = useUserStore((s) => s.user);
  const [pool, setPool] = useState<PhotoPoolItem[] | null>(null);
  const [defaultIndex, setDefaultIndex] = useState(0);
  const [serverDate, setServerDate] = useState('');
  const [offset, setOffset] = useState(0);
  const [fetching, setFetching] = useState(false);

  const isAuthenticated = !!user;
  const canBrowse = isAuthenticated && !!queryKey;

  const fetchPool = useCallback(async () => {
    if (!queryKey || fetching || pool) return;
    setFetching(true);
    try {
      const data = await getPhotoPool(queryKey);
      setPool(data.photos);
      setDefaultIndex(data.default_index);
      setServerDate(data.server_date);
      const stored = getStoredOffset(queryKey, data.server_date);
      setOffset(stored);
    } catch {
      setPool([]);
    } finally {
      setFetching(false);
    }
  }, [queryKey, fetching, pool]);

  const navigate = useCallback((direction: 1 | -1) => {
    if (!pool || pool.length < 2 || !queryKey) return;
    const newOffset = (offset + direction + pool.length) % pool.length;
    setOffset(newOffset);
    setStoredOffset(queryKey, serverDate, newOffset);
  }, [pool, offset, queryKey, serverDate]);

  // Determine which image to show — and WHOSE it is.
  let displaySrc = src;
  let displayAttribution = attribution ?? null;
  if (pool && pool.length > 0) {
    const idx = (defaultIndex + offset) % pool.length;
    const photo = pool[idx];
    displaySrc = size === 'thumb-sm' ? (photo.thumb_url || photo.url) : photo.url;
    // The pool row's OWN photographer, not the one the page was rendered
    // with. `getPhotoPool` only returns creditable rows, so both halves are
    // present; a row that somehow lacks either credits nobody rather than
    // half-crediting someone.
    displayAttribution = (photo.photographer && photo.unsplash_link)
      ? { photographer: photo.photographer, unsplash_link: photo.unsplash_link }
      : null;
  }

  const notify = onAttributionChange;
  const shownPhotographer = displayAttribution?.photographer ?? '';
  const shownLink = displayAttribution?.unsplash_link ?? '';
  React.useLayoutEffect(() => {
    // useLayoutEffect, NOT useEffect (r775 finding 3). A passive effect runs
    // AFTER paint, so pressing an arrow painted the new photograph beside the
    // previous photographer's name for a frame — a wrong credit, which is
    // worse than a missing one. A layout effect runs before the browser
    // paints, so the image and the name change together.
    //
    // Keyed on the VALUES, not the object: a fresh object every render would
    // make this fire forever.
    notify(shownPhotographer && shownLink
      ? { photographer: shownPhotographer, unsplash_link: shownLink }
      : null);
  }, [notify, shownPhotographer, shownLink]);

  const showArrows = canBrowse && pool && pool.length >= 2;

  return (
    <div className={`browseable-image${className ? ` ${className}` : ''}`}>
      <HeroImage
        src={displaySrc}
        fallbackKey={fallbackKey}
        size={size}
        onBareChange={onBareChange}
        showLabel={showLabel}
      />
      {canBrowse && (
        <div className="browseable-image__arrows" onMouseEnter={fetchPool} onTouchStart={fetchPool}>
          {showArrows && (
            <>
              <button
                className="browseable-image__arrow browseable-image__arrow--left"
                onClick={(e) => { e.stopPropagation(); e.preventDefault(); navigate(-1); }}
                aria-label="Previous photo"
              >
                <ChevronLeft size={16} />
              </button>
              <button
                className="browseable-image__arrow browseable-image__arrow--right"
                onClick={(e) => { e.stopPropagation(); e.preventDefault(); navigate(1); }}
                aria-label="Next photo"
              >
                <ChevronRight size={16} />
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default BrowseableImage;
